import { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import {
  Lightbulb,
  CircleCheck,
  ArrowUpRight,
  Home,
  Map,
  Building2,
  MapPin,
  Newspaper,
  KeyRound,
  Users,
  LayoutGrid,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSite } from '@/context/SiteSettingsContext'
import type { MegaMenuConfig, MegaMenuSection } from '@/lib/megaMenu'
import { useCityStore } from '@/store/cityStore'
import { useAnchoredPosition } from '@/hooks/useAnchoredPosition'
import { cn } from '@/lib/utils'
import { containsTarget } from '@/lib/dom'

const SECTION_ICONS: Record<string, LucideIcon> = {
  'buy-home': Home,
  'rent-home': KeyRound,
  'land-plot': Map,
  commercial: Building2,
  'popular-areas': MapPin,
  pg: Users,
  insights: Lightbulb,
  articles: Newspaper,
  offerings: LayoutGrid,
}

interface NavMegaMenuProps {
  label: string
  config: MegaMenuConfig | ((city: string) => MegaMenuConfig)
}

function MegaMenuBadge({ type }: { type: 'new' | 'free' }) {
  if (type === 'new') {
    return (
      <span className="ml-1.5 inline-flex rounded bg-primary-500 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white">
        New
      </span>
    )
  }
  return (
    <span className="ml-1.5 inline-flex rounded bg-success-500 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white">
      Free
    </span>
  )
}

function PromoCard({ promo, onClose }: { promo: MegaMenuConfig['promo']; onClose: () => void }) {
  if (promo.variant === 'post-property') {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-xl bg-linear-to-br from-primary-50 to-secondary-50 p-4">
        <p className="font-display text-sm font-bold leading-snug text-neutral-900">{promo.title}</p>
        {promo.subtitle && <p className="mt-1 text-xs text-neutral-600">{promo.subtitle}</p>}
        {promo.image && (
          <img src={promo.image} alt="" className="mt-3 aspect-4/3 w-full rounded-lg object-cover" />
        )}
        <Button asChild size="sm" className="mt-4 w-full rounded-full">
          <Link to={promo.cta.href} onClick={onClose}>
            {promo.cta.label}
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col rounded-xl border border-primary-100 bg-primary-50/60 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-white">
            <Lightbulb className="h-4 w-4" />
          </span>
          <p className="text-[10px] font-bold uppercase tracking-wider text-primary-600">Introducing</p>
        </div>
        <ArrowUpRight className="h-4 w-4 shrink-0 text-primary-400" />
      </div>
      <p className="mt-2 font-display text-base font-bold text-neutral-900">{promo.title}</p>
      <ul className="mt-3 flex-1 space-y-2">
        {promo.bullets?.map((item) => (
          <li key={item} className="flex items-start gap-2 text-xs text-neutral-700">
            <CircleCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-500" />
            {item}
          </li>
        ))}
      </ul>
      <Link
        to={promo.cta.href}
        onClick={onClose}
        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
      >
        {promo.cta.label} <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  )
}

function SectionSidebar({
  sections,
  activeId,
  onSelect,
}: {
  sections: readonly MegaMenuSection[]
  activeId: string
  onSelect: (id: string) => void
}) {
  const site = useSite()

  return (
    <aside className="flex w-[190px] shrink-0 flex-col border-r border-neutral-100 bg-neutral-50 py-4 pl-3 pr-2">
      <nav className="flex flex-col gap-0.5">
        {sections.map((section) => {
          const Icon = SECTION_ICONS[section.id]
          const active = activeId === section.id
          return (
            <button
              key={section.id}
              type="button"
              onMouseEnter={() => onSelect(section.id)}
              onFocus={() => onSelect(section.id)}
              onClick={() => onSelect(section.id)}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide transition-colors',
                active
                  ? 'bg-white text-primary-700 shadow-sm ring-1 ring-neutral-100'
                  : 'text-neutral-600 hover:bg-white/70 hover:text-primary-600',
              )}
            >
              {Icon && <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />}
              <span className="flex flex-wrap items-center">
                {section.label}
                {section.badge === 'new' && <MegaMenuBadge type="new" />}
              </span>
            </button>
          )
        })}
      </nav>
      <p className="mt-auto px-1 pt-6 text-[10px] leading-relaxed text-neutral-400">
        Contact us on{' '}
        <a href={`tel:${site.phoneTel}`} className="font-semibold text-neutral-600 hover:text-primary-600">
          {site.phone}
        </a>
        <br />
        (9AM–7PM IST)
      </p>
    </aside>
  )
}

export function NavMegaMenu({ label, config }: NavMegaMenuProps) {
  const site = useSite()
  const [open, setOpen] = useState(false)
  const [activeSectionId, setActiveSectionId] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const { cityName } = useCityStore()

  const menu = useMemo(
    () => (typeof config === 'function' ? config(cityName) : config),
    [config, cityName],
  )

  const activeSection = menu.sections.find((s) => s.id === activeSectionId) ?? menu.sections[0]
  const coords = useAnchoredPosition(open, triggerRef, panelRef, { panelAnchorRatio: 0.35 })

  useEffect(() => {
    if (!activeSectionId && menu.sections[0]) {
      setActiveSectionId(menu.sections[0].id)
    }
  }, [menu.sections, activeSectionId])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containsTarget(rootRef.current, e.target)) return
      if (containsTarget(panelRef.current, e.target)) return
      setOpen(false)
    }
    if (!open) return
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const close = () => setOpen(false)

  const handleEnter = () => setOpen(true)
  const handleLeave = (e: React.MouseEvent) => {
    if (containsTarget(panelRef.current, e.relatedTarget)) return
    setOpen(false)
  }

  const panel = open ? (
    <div
      ref={panelRef}
      role="menu"
      className={cn(
        'fixed z-[110] hidden w-[min(880px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-2xl lg:block',
        !coords.ready && 'invisible',
      )}
      style={{ top: coords.top, left: coords.left }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setOpen(false)}
    >
      <div className="flex min-h-[300px]">
        <SectionSidebar
          sections={menu.sections}
          activeId={activeSection.id}
          onSelect={setActiveSectionId}
        />

        <div className="flex min-w-0 flex-1 gap-6 p-5">
          {activeSection.columns.map((col) => (
            <div key={col.heading} className="min-w-[160px] flex-1">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                {col.heading}
              </p>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.href + link.label}>
                    <Link
                      to={link.href}
                      onClick={close}
                      className="inline-flex cursor-pointer items-center text-sm text-neutral-700 transition-colors hover:text-primary-600"
                    >
                      {link.label}
                      {link.badge === 'free' && <MegaMenuBadge type="free" />}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <aside className="hidden w-[220px] shrink-0 border-l border-neutral-100 p-4 xl:block">
          <PromoCard promo={menu.promo} onClose={close} />
        </aside>
      </div>

      <div className="border-t border-neutral-100 bg-neutral-50/50 px-5 py-2 text-[11px] text-neutral-500">
        Email us at{' '}
        <a href={`mailto:${site.email}`} className="font-medium text-primary-600 hover:underline">
          {site.email}
        </a>
        , or call us at{' '}
        <a href={`tel:${site.phoneTel}`} className="font-medium text-primary-600 hover:underline">
          {site.phone}
        </a>
      </div>
    </div>
  ) : null

  return (
    <div
      ref={rootRef}
      className="relative shrink-0"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'relative cursor-pointer whitespace-nowrap px-2 py-2 text-[13px] font-semibold transition-colors xl:px-2.5 xl:text-sm',
          open ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600',
          open && 'after:absolute after:bottom-0 after:left-1 after:right-1 after:h-0.5 after:rounded-full after:bg-primary-500',
        )}
      >
        {label}
      </button>

      {/* Hover bridge — keeps menu open while moving pointer down */}
      {open && (
        <span
          className="absolute left-1/2 top-full z-90 hidden h-2 w-24 -translate-x-1/2 lg:block"
          aria-hidden
        />
      )}

      {typeof document !== 'undefined' && panel ? createPortal(panel, document.body) : null}
    </div>
  )
}
